// Raw event/category identifiers used as internal lookup keys (matched
// against API field maps and state comparisons) must stay untranslated.
// These maps resolve the raw identifier to a translation key ONLY for
// display purposes.
export const FUEL_USAGE_EVENT_LABEL_KEYS: Record<string, string> = {
  Overspeeding: 'fuel_usage_event_overspeeding',
  Underspeeding: 'fuel_usage_event_underspeeding',
  'Incorrect Gear Speed Ratio': 'fuel_usage_event_incorrect_gear_speed_ratio',
  'Short Idling': 'fuel_usage_event_short_idling',
  'Long Idling': 'fuel_usage_event_long_idling',
  'Queuing - Pre Loading': 'fuel_usage_event_queuing_pre_loading',
  'Queuing - Pre Dumping': 'fuel_usage_event_queuing_pre_dumping',
  'Road Conditions': 'fuel_usage_event_road_conditions',
  'Tire Underpressure': 'fuel_usage_event_tire_underpressure',
  'Excess Fuel Usage': 'fuel_usage_excess_fuel_usage',
};

export const getFuelUsageEventLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  eventName?: string | null
): string => {
  if (!eventName) return '';
  const key = FUEL_USAGE_EVENT_LABEL_KEYS[eventName];
  return key ? t(key) : eventName;
};

export const FUEL_USAGE_MEASURE_LABEL_KEYS: Record<string, string> = {
  Consumption: 'fuel_usage_dropdown_consumption',
  'Co2 Emission': 'fuel_usage_dropdown_co2_emission',
  'Num of Idle Events': 'fuel_usage_dropdown_num_of_idle_events',
  'Total Duration of Idle Events':
    'fuel_usage_dropdown_total_duration_of_idle_events',
  'Loaded Fuel Consumption': 'fuel_usage_dropdown_loaded_fuel_consumption',
  'Unloaded Fuel Consumption': 'fuel_usage_dropdown_unloaded_fuel_consumption',
  'Avg Fuel Rate': 'fuel_usage_dropdown_avg_fuel_rate',
  'Fuel Consumed Per Ton': 'fuel_usage_dropdown_fuel_consumed_per_ton',
  Overspeeding: 'fuel_usage_event_overspeeding',
  Underspeeding: 'fuel_usage_event_underspeeding',
  Gears: 'fuel_usage_dropdown_gears',
  'Short Idling': 'fuel_usage_event_short_idling',
  'Long Idling': 'fuel_usage_event_long_idling',
  Queuing: 'fuel_usage_dropdown_queuing',
};

export const getFuelUsageMeasureLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  measure?: string | null
): string => {
  if (!measure) return '';
  const key = FUEL_USAGE_MEASURE_LABEL_KEYS[measure];
  return key ? t(key) : measure;
};

export function getFuelUsageDataUnit(type: string, unitSystem: string) {
  type = type.toLowerCase().replace(/ /g, '');
  if (
    type === 'consumption' ||
    type === 'loaded_fuel_consumption' ||
    type === 'unloaded_fuel_consumption' ||
    type === 'total_overspeed_fuel' ||
    type === 'total_underspeed_fuel' ||
    type === 'total_gear_fuel' ||
    type === 'total_short_idle_fuel' ||
    type === 'total_long_idle_fuel' ||
    type === 'total_pre_loading_fuel' ||
    type === 'total_pre_dumping_fuel' ||
    type === 'overspeeding' ||
    type === 'underspeeding' ||
    type === 'gears' ||
    type === 'short_idling' ||
    type === 'long_idling' ||
    type === 'queuing'
  ) {
    return unitSystem === 'metric' ? 'L' : 'Gal';
  }
  if (type === 'co2_emission') {
    return 'Kg';
  }
  if (type === 'fuel_consumed_per_ton') {
    return unitSystem === 'metric' ? 'L/ton' : 'Gal/Ton';
  }
  if (type === 'co2emission') {
    return 'Kg';
  }
  if (type === 'total_duration_of_idle_events') {
    return 'Hrs';
  }

  if (type === 'fuel') {
    return unitSystem === 'imperial' ? 'Gal' : 'L';
  }

  if (type === 'utilization') {
    return '%';
  }

  if (type === 'idlinghours') {
    return 'Hrs';
  }

  if (type === 'productivehours') {
    return 'Hrs';
  }
  if (type === 'avgfuelrate' || type === 'avg_fuel_rate') {
    return unitSystem === 'imperial' ? 'Gal/h' : 'L/h';
  }
  if (type === 'enginehours') {
    return 'Hrs';
  }
  if (type === 'productionrate') {
    return unitSystem === 'imperial' ? 'Tons / h' : 'Tons / h';
  }
}

export const getStartAndEndDateFromRange = (
  range: number,
  yearTillDate = false
) => {
  const currentDate = new Date();
  const endDate = new Date(new Date().setDate(currentDate.getDate() - 1)); // currentDate
  let startDate;
  if (yearTillDate) {
    startDate = `${endDate.getFullYear()}-01-01`;
  } else {
    if (range > 1) {
      startDate = new Date(
        new Date(endDate).setDate(endDate.getDate() - range + 1)
      )
        .toISOString()
        .slice(0, 10);
    } else {
      startDate = endDate.toISOString().slice(0, 10);
    }
  }
  return {
    endDate: endDate.toISOString().slice(0, 10),
    startDate,
    noOfDays: range,
  };
};

export const getDaysSinceStartOfYear = (): number => {
  const currentDate = new Date(); // Current date
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1); // Jan 1st of the current year

  const differenceInMilliseconds =
    currentDate.getTime() - startOfYear.getTime();

  const daysSinceStartOfYear = Math.floor(
    differenceInMilliseconds / (1000 * 60 * 60 * 24)
  );

  return daysSinceStartOfYear;
};
